"use client";

import React from "react";

import { cn } from "@/lib/utils";
import { animate, useMotionValue, type Variants } from "framer-motion";

import { MotionDiv, MotionSpan } from "@/components/global/utils/motionTags";
import { EASE_OUT, STACK_STEP } from "@/components/global/utils/motionTiming";
import { parseStat, useCountUp } from "@/components/bastian-keller/statValue";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

/** Top-right corner cut, 22px of the Figma row. */
const NOTCH =
  "polygon(0 0, calc(100% - 2.75rem) 0, 100% 2.75rem, 100% 100%, 0 100%)";

const ROW_WIPE = 0.8;

/**
 * The count runs longer than the panel it sits in. The wipe is over quickly
 * because it is scenery, but a figure climbing is the thing you are meant to
 * watch — given the same 0.8s it reads as already-finished by the time the eye
 * arrives.
 */
const COUNT_RUN = 1.15;

/**
 * The panel opens left to right, so the figure is uncovered before the label and
 * the row reads in the order you would read it. The rise is the same 2.5rem the
 * copy column opposite uses, so both sides of the portrait arrive on one move.
 */
const rowVariants: Variants = {
  hidden: { opacity: 0, y: "2.5rem", clipPath: "inset(0% 100% 0% 0%)" },
  shown: {
    opacity: 1,
    y: "0rem",
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: ROW_WIPE, ease: EASE_OUT },
  },
};

/**
 * The "+" lands after its figure has stopped climbing rather than riding in with
 * it — the number reads as settling on a value and then being qualified.
 */
const plusVariants: Variants = {
  hidden: { opacity: 0 },
  shown: {
    opacity: 1,
    transition: { duration: 0.4, ease: EASE_OUT, delay: COUNT_RUN * 0.75 },
  },
};

/**
 * Motion-off stand-in. Like the hero's, it resets every property the variants
 * above set rather than only fading: the mode is not known at first render, so a
 * row can mount clipped and then have its variants swapped for this one — and
 * framer leaves an inline property in place once a variants object stops
 * mentioning it.
 */
const staticVariants: Variants = {
  hidden: { opacity: 0 },
  shown: {
    opacity: 1,
    y: "0rem",
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.5, ease: EASE_OUT },
  },
};

type Stat = PageBastianKellerSchema["heroStats"][number];

function StatRow({
  stat,
  index,
  start,
  reduceMotion,
}: {
  stat: Stat;
  index: number;
  start: number;
  reduceMotion: boolean;
}) {
  const parsed = parseStat(stat?.value);

  /*
    The figure climbs on its own linear ramp rather than off the panel's eased
    variant. EASE_OUT is front-loaded enough that a count riding it is ~76% done
    in the first quarter and then crawls — the same reason `useReveal` hands out
    a separate `linear` track for counters.
  */
  const progress = useMotionValue(reduceMotion ? 1 : 0);

  React.useEffect(() => {
    if (reduceMotion) {
      progress.set(1);
      return;
    }

    const controls = animate(progress, 1, {
      duration: COUNT_RUN,
      ease: "linear",
      delay: start + index * STACK_STEP,
    });

    return () => controls.stop();
  }, [progress, reduceMotion, start, index]);

  const display = useCountUp(progress, parsed);

  // only a trailing "+" is the muted qualifier; a unit like the "K" of 50K
  // belongs to the figure and stays dark
  const suffix = parsed?.suffix ?? "";
  const plus = suffix.endsWith("+") ? "+" : "";
  const unit = plus ? suffix.slice(0, -1) : suffix;

  return (
    <MotionDiv
      variants={reduceMotion ? staticVariants : rowVariants}
      className="w-full"
    >
      <div
        style={{ clipPath: NOTCH }}
        className="flex w-full items-center bg-[#052920]/[.03] py-[2.25rem] pl-[3rem]"
      >
        {/*
          Both spans sit in the same grid cell, so the figure's slot is as wide as
          its final value from the first frame.

          Without that the row reflows mid-count: "0K+" is narrower than "50K+",
          the label beside it is free to take the slack, and it drops a line at the
          exact moment the last digit appears — jolting the whole block, which is
          anchored by its bottom edge. Reserving the width costs nothing, since the
          value is known before the count starts.

          From lg the slot is also pinned to a fixed 15.625rem. The design varies
          the gap per row (30/14/38px against figures of 95/111/87px) purely so the
          labels all start at the same x — 24 + 125 = 149px into a 405px row — so
          the column width is the rule and the gap falls out of it.
        */}
        <p className="m-0 grid w-[15.625rem] shrink-0 font-geist text-[4.75rem] font-medium leading-[1.26] text-black opacity-80 [&>span]:[text-box-edge:cap_alphabetic] [&>span]:[text-box-trim:trim-both]">
          <span
            aria-hidden
            className="invisible col-start-1 row-start-1 whitespace-nowrap"
          >
            {stat?.value}
          </span>

          <span className="col-start-1 row-start-1 whitespace-nowrap">
            {parsed ? (
              <>
                {parsed.prefix}
                {/*
                  framer writes the counter straight onto textContent, so it has to
                  be this span's only child — the unit rides alongside it.
                */}
                <MotionSpan>{display}</MotionSpan>
                {unit}
              </>
            ) : (
              stat?.value
            )}
            {plus && (
              <MotionSpan
                variants={reduceMotion ? staticVariants : plusVariants}
                className="inline-block text-black/40"
              >
                {plus}
              </MotionSpan>
            )}
          </span>
        </p>

        {stat?.label && (
          <p className="m-0 w-[32rem] font-geist text-[2.25rem] font-medium leading-[1.26] text-black/40">
            {stat.label}
          </p>
        )}
      </div>
    </MotionDiv>
  );
}

export default function HeroStats({
  stats,
  start,
  reduceMotion,
  className,
}: {
  stats?: Stat[];
  start: number;
  reduceMotion: boolean;
  className?: string;
}) {
  if (!stats?.length) return null;

  return (
    <MotionDiv
      initial={reduceMotion ? false : "hidden"}
      animate="shown"
      transition={{ delayChildren: start, staggerChildren: STACK_STEP }}
      className={cn("flex w-full flex-col gap-[0.75rem]", className)}
    >
      {stats.map((stat, index) => (
        <StatRow
          key={index}
          stat={stat}
          index={index}
          start={start}
          reduceMotion={reduceMotion}
        />
      ))}
    </MotionDiv>
  );
}
